'use client';

import { APIProvider, Map, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import { FC, useEffect, useRef, useState } from 'react';
import styles from './DealerMap.module.scss';
// import { Store, StoresData } from '@/src/app/(root)/find-a-dealer/MapSection/Types/types';
import smoothPanAndZoom from './smoothPanAndZoom';
import { useMediaQuery } from 'usehooks-ts';

const DealersMap = ({
  stores,
  postalData,
  userInfo,
  selectedStoreCode,
  setSelectedStoreCode,
  centerLat,
  centerLon,
}) => {
  const defaultLocation = { lat: parseFloat(centerLat), lng: parseFloat(centerLon) };
  const [storeLocation, setStoreLocation] = useState([]);
  const [postalInfo, setPostalInfo] = useState();
  const [nearbyStores, setNearbyStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (stores.length > 0) {
      setStoreLocation(stores);
      setLoading(false);
    }
  }, [stores, userInfo]);

  useEffect(() => {
    if (postalData?.nearbyStores?.length > 0) {
      setNearbyStores(postalData.nearbyStores);
    } else {
      setNearbyStores([]);
    }
    if (postalData?.postcodeInfo?.latitude && postalData?.postcodeInfo?.longitude) {
      setPostalInfo(postalData?.postcodeInfo);
    }
  }, [postalData]);

  if (loading || !defaultLocation.lat || !defaultLocation.lng) {
    return null;
  }

  return (
    <div className={styles.map}>
      <APIProvider apiKey="AIzaSyDxAkc0WR1o6FJcJVgAhZ1UNEK7DDM8jgE" libraries={['geometry']}>
        <Map
          defaultCenter={defaultLocation}
          defaultZoom={7.7}
          mapId="6fbca40ab51e1cf0"
          gestureHandling="true"
          scrollwheel={true}
          minZoom={6}
          maxZoom={12}
          disableDefaultUI={true}
        >
          <Markers
            userInfo={userInfo}
            points={storeLocation}
            postalInfo={postalInfo}
            nearbyStores={nearbyStores}
            selectedStoreCode={selectedStoreCode}
            setSelectedStoreCode={setSelectedStoreCode}
            centerLat={centerLat}
            centerLon={centerLon}
          />
        </Map>
      </APIProvider>
    </div>
  );
};

export default DealersMap;

// type MarkersProps = {
//   userInfo: any;
//   points: Store[];
//   selectedStoreCode: Store | null | undefined;
//   setSelectedStoreCode: React.Dispatch<React.SetStateAction<Store | null | undefined>>;
// };

const Markers = ({
  userInfo,
  points,
  postalInfo,
  nearbyStores,
  selectedStoreCode,
  setSelectedStoreCode,
  centerLat,
  centerLon,
}) => {
  const map = useMap();
  const defaultCenter = { lat: parseFloat(centerLat), lng: parseFloat(centerLon) };
  const isMounted = useRef(false);
  const [defaultZoom, setDefaultZoom] = useState(7.7);

  const xl = useMediaQuery('(max-width: 834px)');
  const l = useMediaQuery('(max-width: 480px)');

  useEffect(() => {
    if (xl && !l) {
      setDefaultZoom(7.1);
      map?.setZoom(7.1);
    } else if (l) {
      setDefaultZoom(7.4);
      map?.setZoom(7.4);
    } else {
      setDefaultZoom(7.7);
      map?.setZoom(7.7);
    }
  }, [xl, l, map]);

  const handleStoreClick = (store) => {
    if (!map || !isMounted.current) return;

    setSelectedStoreCode(store);

    const startCenter = map.getCenter() || new google.maps.LatLng(defaultCenter);
    const currentZoom = map.getZoom();

    smoothPanAndZoom(
      map,
      { lat: startCenter.lat(), lng: startCenter.lng() },
      {
        lat: parseFloat(store?.location?.fields.lat?.value),
        lng: parseFloat(store?.location?.fields.lng?.value),
      },
      currentZoom,
      13,
      1500
    );
  };

  useEffect(() => {
    if (!map) return;
    if (!isMounted.current) return;

    if (selectedStoreCode === null) {
      const currentCenter = map.getCenter();
      if (!currentCenter) return;

      smoothPanAndZoom(
        map,
        { lat: currentCenter.lat(), lng: currentCenter.lng() },
        defaultCenter,
        map.getZoom() ?? 13,
        defaultZoom,
        1500
      );
    }
  }, [selectedStoreCode, defaultZoom]);

  useEffect(() => {
    if (!map || !window.google) return;
    if (!isMounted.current) return;
    if (
      userInfo?.location?.fields?.lat?.value === 0 &&
      userInfo?.location?.fields?.lng?.value === 0
    ) {
      const currentCenter = map.getCenter();
      if (!currentCenter) return;

      smoothPanAndZoom(
        map,
        { lat: currentCenter.lat(), lng: currentCenter.lng() },
        defaultCenter,
        map.getZoom() ?? 7.7,
        7.7,
        1500
      );
      setSelectedStoreCode(null);
      return;
    }

    if (userInfo?.location?.fields?.lat?.value && points.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      bounds.extend(
        new window.google.maps.LatLng(
          userInfo.location?.fields.lat?.value,
          userInfo.location?.fields.lng?.value
        )
      );

      points.forEach((store) => {
        bounds.extend(
          new window.google.maps.LatLng(
            parseFloat(store?.fields?.location?.fields?.lat?.value),
            parseFloat(store?.fields?.location?.fields?.lng?.value)
          )
        );
      });

      const currentCenter = map.getCenter() || new google.maps.LatLng(defaultCenter);
      const currentZoom = map.getZoom();

      const boundsCenter = bounds.getCenter();

      smoothPanAndZoom(
        map,
        { lat: currentCenter.lat(), lng: currentCenter.lng() },
        { lat: boundsCenter.lat(), lng: boundsCenter.lng() },
        currentZoom,
        10,
        1500
      );
    }
  }, [userInfo, points, setSelectedStoreCode]);

  useEffect(() => {
    if (!map || !window.google) return;
    if (!isMounted.current) return;
    if (nearbyStores?.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      bounds.extend(new window.google.maps.LatLng(postalInfo?.latitude, postalInfo?.longitude));

      nearbyStores.forEach((store) => {
        bounds.extend(
          new window.google.maps.LatLng(parseFloat(store?.latitude), parseFloat(store?.longitude))
        );
      });

      const currentCenter = map.getCenter() || new google.maps.LatLng(defaultCenter);
      const currentZoom = map.getZoom();

      const boundsCenter = bounds.getCenter();

      smoothPanAndZoom(
        map,
        { lat: currentCenter.lat(), lng: currentCenter.lng() },
        { lat: boundsCenter.lat(), lng: boundsCenter.lng() },
        currentZoom,
        10,
        1500
      );
    }
  }, [postalInfo, nearbyStores]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  return (
    <>
      {points?.map((store, index) => (
        <AdvancedMarker
          position={{
            lat: parseFloat(store?.fields?.location?.fields?.lat?.value),
            lng: parseFloat(store?.fields?.location?.fields?.lng?.value),
          }}
          key={
            store?.fields?.storeCode?.value === '/'
              ? `${store?.fields?.storeName?.value}-${index}`
              : store?.fields?.storeCode?.value
          }
          onClick={() => handleStoreClick(store?.fields)}
        >
          <Pin
            background="#121212"
            borderColor={
              nearbyStores?.some((s) => s.storeCode === store?.fields?.storeCode?.value)
                ? '#005fcc'
                : '#121212'
            }
            glyphColor="white"
            scale={
              selectedStoreCode?.storeCode?.value === store?.fields?.storeCode?.value ||
              nearbyStores?.some((s) => s.storeCode === store?.fields?.storeCode?.value)
                ? 1.2
                : 0.6
            }
          />
        </AdvancedMarker>
      ))}
    </>
  );
};
